import React, { forwardRef, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

interface CheckboxProps {
  labelFor?: string;
  label?: React.ReactNode;
  checkboxValue?: string;
  onChange?: (...args: never[]) => void;
  checked?: boolean;
  indeterminate?: boolean;
  customStylesLabel?: string;
  inputCustomClass?: string;
  labelTextClass?: string;
  disabled?: boolean;
  'data-testid'?: string;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      labelFor,
      label,
      checkboxValue,
      onChange,
      checked,
      indeterminate,
      customStylesLabel,
      inputCustomClass,
      labelTextClass,
      disabled,
      ...rest
    },
    ref
  ) => {
    const defaultRef = useRef<HTMLInputElement>(null);
    const resolvedRef = (ref as React.RefObject<HTMLInputElement | null>) || defaultRef;

    useEffect(() => {
      if (resolvedRef.current) {
        resolvedRef.current.indeterminate = !!indeterminate;
      }
    }, [indeterminate, resolvedRef]);

    const labelClasses = twMerge('flex items-center', customStylesLabel);

    const inputClasses = twMerge(
      'w-4 h-4 checked:accent-navy-800 indeterminate:accent-navy-800',
      !disabled && 'cursor-pointer',
      inputCustomClass,
    );

    const labelTextClasses = twMerge('ml-2', labelTextClass);

    return (
      <label htmlFor={labelFor} className={labelClasses}>
        <input
          data-testid={`checkbox-${labelFor || 'custom'}`}
          className={inputClasses}
          type="checkbox"
          id={labelFor}
          value={checkboxValue}
          checked={checked}
          onChange={() => {
            if (!disabled) {
              (onChange as ((value: { userId: string | undefined; hasAccess: boolean }) => void) | undefined)?.({
                userId: checkboxValue,
                hasAccess: !checked,
              });
            }
          }}
          disabled={disabled}
          ref={resolvedRef}
          {...rest}
        />
        <span className={labelTextClasses}>{label}</span>
      </label>
    );
  }
);

export default Checkbox;
